import type zhCN from './zh-CN'

const enUS: typeof zhCN = {
  common: {
    appName: 'HiBro Developer Platform',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    copy: 'Copy',
    copied: 'Copied',
    edit: 'Edit',
    reset: 'Reset',
    delete: 'Delete',
    back: 'Back',
    loading: 'Loading…',
    empty: 'No data',
    show: 'Show',
    hide: 'Hide',
    enabled: 'Enabled',
    disabled: 'Disabled',
    createdAt: 'Created at',
    cropTitle: 'Crop Image',
    cropZoom: 'Zoom',
    cropFailed: 'Could not crop the image. Please try again.',
    preview: 'Preview',
    download: 'Download',
  },
  lang: {
    label: 'Language',
    'zh-CN': '简体中文',
    'en-US': 'English',
  },
  spaceSwitcher: {
    label: 'Switch space',
    placeholder: 'Select space',
    personal: 'Personal spaces',
    workspace: 'Workspaces',
    role: {
      owner: 'Owner',
      admin: 'Admin',
      member: 'Member',
    },
    logout: 'Sign out',
  },
  env: {
    current: 'Environment',
  },
  nav: {
    workbench: 'Workbench',
    accountSettings: 'Account',
    developerInfo: 'Developer Info',
    sdkDownload: 'SDK Download',
  },
  appNav: {
    groupAppSettings: 'App Settings',
    groupVersion: 'Version Management',
    groupDev: 'Development',
    dashboard: 'Dashboard',
    backToHome: 'Back to Home',
    allVersions: 'Versions',
    newVersion: 'Add Versions',
    appId: 'APP ID',
    moduleId: 'App ID',
    credentials: 'Credentials',
    scope: 'Permissions',
    events: 'Events & Callbacks',
    security: 'Security',
    products: 'Subscription Products',
    accountSettings: 'Account Settings',
  },
  appStatus: {
    development: 'In Development',
    published: 'Published',
    offline: 'Offline',
  },
  workbench: {
    title: 'Workbench',
    subtitle: 'Create and manage your open platform apps',
    createApp: 'Create New App',
    createTitle: 'Create App',
    createClose: 'Close',
    createAppIdLabel: 'App ID',
    createAppIdPlaceholder: '2–32 chars (a–z, 0–9, -). Starts with a letter.',
    createAppIdHint: 'Cannot be changed after creation. Write it to the main package manifest id.',
    createAgreement: 'I have read and agree to the Developer Agreement',
    createSubmit: 'Create',
    createSuccess: 'App created',
    createFailed: 'Could not create the app. Try again.',
    nameLabel: 'App Name',
    namePlaceholder: 'Enter app name',
    descLabel: 'Description',
    descPlaceholder: 'Briefly describe the app',
    roleTitle: 'Developer Role',
    developerId: 'Developer ID : ',
    accountSettings: 'Account Settings',
    activityTitle: 'Recent Activity',
    viewAll: 'View All',
    activityEmpty: 'No activity yet',
    activityLoadFailed: 'Failed to load activity',
    activityToday: 'Today {{time}}',
    activityYesterday: 'Yesterday {{time}}',
    activityDrawerTitle: 'Recent Activity',
    searchPlaceholder: 'Search by app name',
    filterAll: 'All',
    filterPending: 'Pending Release',
    filterPublished: 'Published',
    collaboration: 'Collaboration',
    version: 'Version: ',
    owner: 'Owner: ',
    me: 'Me',
    updatedAt: 'Updated {{time}}',
    distributionSpace: 'Workspace Custom',
    distributionPublic: 'All Workspaces',
    statusInReview: 'In Review',
    statusPending: 'Pending',
    statusPublished: 'Published',
    statusDeveloping: 'Draft',
    statusRejected: 'Rejected',
    statusDelisted: 'Delisted',
    statusDisabled: 'Disabled',
    statusArchived: 'Archived',
    emptyApps: 'No apps yet',
    loadFailed: 'Failed to load apps',
    guideTitle: 'Welcome to the HiBro Developer Console',
    guideSubtitle: 'Follow the steps below to publish your first application.',
    guideStep1Title: 'Create App',
    guideStep1Desc:
      'Upload main package (.tgz) for auto-parsing of app details and ID assignment.',
    guideStep2Title: 'Complete Listing Info & Submit Version',
    guideStep2Desc:
      'Complete listing details, add platform packages if needed, and submit for review.',
    guideStep3Title: 'Submit for Review',
    guideStep3Desc: 'App will be published automatically once approved.',
    guideCta: 'Create First App Now',
    paginationPrev: 'Previous',
    paginationNext: 'Next',
    paginationPage: 'Page {{page}}',
  },
  versions: {
    title: 'Versions',
    create: 'Add Versions',
    saveSuccess: 'Saved',
    submitSuccess: 'Version submitted for review',
    submitScheduled: 'Approved. It will go live at the scheduled time.',
    submitReleased: 'Version is live',
    issuedCredentialsTitle: 'App credentials created',
    issuedCredentialsBody:
      'The App Secret is shown only once. Copy and save it now.',
    scheduleUpdated: 'Release time updated',
    empty: 'No versions yet. Click "Add Versions" to start.',
    emptyGuide:
      'No versions yet. Click "+ Add Versions" to upload the first package.',
    version: 'Version',
    platforms: 'Platforms',
    status: 'Status',
    onlineTime: 'Release Time',
    lastUpdated: 'Last Updated',
    operator: 'Operator',
    changelog: 'Changelog',
    actions: 'Actions',
    filterAll: 'All',
    filterStatusAll: 'All status',
    rollingBack: 'Rolling back…',
    backToList: 'Back to Versions',
    stubUploadTitle: 'Upload package',
    stubUploadDesc:
      'Upload and parse flow will be implemented next. This is a placeholder.',
    stubDetailTitle: 'Version detail',
    stubDetailDesc:
      'Detail form for v{{version}} will be implemented in the next screen.',
    stubDetailMissing: 'Version not found.',
    status_developing: 'Draft',
    status_reviewing: 'In Review',
    status_rejected: 'Rejected',
    status_scheduled: 'Scheduled',
    status_released: 'Released',
    status_delisted: 'Delisted',
    status_disabled: 'Disabled',
    status_archived: 'Archived',
    status_rolling_back: 'Rolling back',
    action_delete: 'Delete',
    action_withdraw: 'Withdraw',
    action_cancelSchedule: 'Cancel schedule',
    action_releaseNow: 'Release now',
    action_delist: 'Delist',
    action_disable: 'Disable',
    action_relist: 'Relist',
    action_enable: 'Enable',
    action_rollback: 'Rollback',
    confirm_delete_title: 'Delete v{{version}}?',
    confirm_delete_body:
      'This cannot be undone. Only draft / rejected versions can be deleted.',
    confirm_delete_ok: 'Delete',
    confirm_withdraw_title: 'Withdraw review?',
    confirm_withdraw_body:
      'The version will return to draft and can be edited and resubmitted.',
    confirm_withdraw_ok: 'Withdraw',
    confirm_cancelSchedule_title: 'Cancel scheduled release?',
    confirm_cancelSchedule_body:
      'The review result is kept and the version becomes archived. You can release it now or schedule it again later.',
    confirm_cancelSchedule_ok: 'Confirm',
    confirm_releaseNow_title: 'Release v{{version}} now?',
    confirm_releaseNow_body: 'This version will become visible to users immediately.',
    confirm_releaseNow_ok: 'Release',
    confirm_delist_title: 'Delist v{{version}}?',
    confirm_delist_body:
      'The live version v{{version}} will be delisted on all platforms. New users cannot install it; existing installs keep working but will not receive updates. The app will have no live version afterwards.',
    confirm_delist_ok: 'Delist',
    confirm_disable_title: 'Disable v{{version}}?',
    confirm_disable_body:
      'v{{version}} will be disabled on all platforms, and no user (including existing installs) will be able to open the app. Data is kept and can be restored by enabling.',
    confirm_disable_ok: 'Disable',
    confirm_relist_title: 'Relist v{{version}}?',
    confirm_relist_body: 'Users will be able to install this app again.',
    confirm_relist_ok: 'Relist',
    confirm_enable_title: 'Enable v{{version}}?',
    confirm_enable_body:
      'All users can use the app again. No re-review is required.',
    confirm_enable_ok: 'Enable',
    confirm_rollback_title: 'Rollback to v{{version}}?',
    confirm_rollback_body:
      'The whole app will switch back to v{{version}}; users will get its packages and listing. Prefer off-peak hours.',
    confirm_rollback_ok: 'Rollback',
    replacesOnline: 'The current live version v{{version}} will go offline.',
    actionSuccess: 'Done',
    reusedFrom: 'Reused v{{version}}',
  },
  versionForm: {
    createTitle: 'Create New Version',
    uploadTitle: 'Upload Packages',
    uploadCreateAppTitle: 'Create App',
    backToUpload: 'Previous step',
    developerAgreement: 'I have read and agree to the Developer Agreement',
    mainPackage: 'Main Package',
    platformBuilds: 'Platform Builds',
    releaseNotes: 'Release Notes',
    appFixedInfo: 'App Fixed Information',
    displayInfo: 'Display Information',
    permissions: 'Permissions',
    events: 'Events',
    releaseSchedule: 'Release Schedule',
    submitReview: 'Submit for Review',
    deleteVersion: 'Delete Version',
    saveVersion: 'Save Version',
    rollbackToThis: 'Rollback to this version',
    rejectedTitle: 'Review Rejected',
    rejectedNoReason:
      'The reviewer did not provide a specific reason. Please contact the admin.',
    dropMainPackage: 'Drag and drop .tgz file here',
    mainPackageHint: '(Supports .tgz format, up to 1 GB)',
    unsupportedPackageFormat: 'Unsupported file format. Only .tgz is allowed.',
    reupload: 'Reupload',
    selectAnother: 'Select Another File',
    retry: 'Retry',
    parsing: 'Parsing, please wait...',
    parseFailed: 'Parsing failed. Please upload a valid package.',
    uploadFailed: 'Upload failed. Please try again.',
    appName: 'App Name',
    appId: 'App ID',
    moduleId: 'App ID',
    versionCode: 'Version',
    platformBuildsHint:
      'Select platforms and upload installation packages:',
    platformBuildsSubHint:
      'Unselected platforms will automatically reuse historical packages if available; otherwise, they will be excluded from this release.',
    platformBuildsFirstHint:
      'Upload installation packages for all platforms:',
    platformBuildsFirstSubHint:
      'The first version must upload a package for every declared platform. Historical reuse is not available.',
    dropPlatformPackage: 'Drag and drop .tgz file here',
    platformPackageHint: '(Supports .tgz format, up to 1 GB)',
    reuseHistorical: 'Unchecked. Reusing historical package v{{version}}',
    reuseHistoricalInline: '(Reusing historical package v{{version}})',
    noHistorical:
      'No historical package. This version will not include this platform.',
    changelogPlaceholder:
      "Describe what's new. Shown in the app detail update history.",
    changelogPlatformsHint:
      'Updated in this version: {{updated}}. Reusing historical packages: {{reused}}. Describe the changes for the updated platforms.',
    multiLangTitle: 'Release Notes Textarea',
    releaseNotesTextarea: 'Release Notes Textarea',
    textPlaceholder: 'Text...',
    confirmContent: 'Confirm the content',
    multiLangHint:
      "The client shows release notes in the user's language; falls back to the default language if empty.",
    defaultChangelog: 'Default release notes ({{lang}})',
    langPlaceholder: 'Enter {{lang}}',
    distributionMethod: 'Distribution Method',
    fixedInfoLocked:
      'Distribution settings are locked after the first version is approved.',
    distSpacePrivate: 'Custom Created',
    distPublic: 'Available in All Workspaces',
    ownerWorkspace: 'Workspace',
    eligibleMembers: 'Eligible Members',
    allMembers: 'All members',
    specifiedMembers: 'Specified members',
    addEligible: 'Add',
    listingCategory: 'Listing Workspace Category',
    catWorkspace: 'Workspace',
    catPersonal: 'Personal Space',
    supportedLanguages: 'Supported Languages',
    langsAdded: 'Added',
    langsAvailable: 'Available Languages',
    addLanguage: 'Add language',
    searchLanguage: 'Search language…',
    defaultTag: '(default)',
    setDefault: 'Set default',
    setDefaultToast:
      '{{lang}} is now the default language. Users without a matching language will see it.',
    appCategory: 'App Category',
    categoryPlaceholder: 'Choose up to 2 categories',
    appTags: 'App Tags',
    selectAppTags: 'Select App Tags',
    tagsPlaceholder: 'Text...',
    tagsFull: 'Limit reached ({{max}}/{{max}})',
    tagsCount: 'Selected {{count}} / {{max}}',
    oneSentence: 'One-sentence Description',
    oneSentencePlaceholder: 'Describe your app in one sentence',
    description: 'Description',
    descriptionPlaceholder: 'Describe features and use cases',
    pcScreenshots: 'PC Screenshots (Shared for Windows/macOS)',
    pcRatioHint: 'Landscape 16:9, ≤ 5 MB each, 1–6 per language',
    mobileScreenshots: 'Mobile Screenshots (Shared for iOS/Android)',
    mobileRatioHint: 'Portrait 9:16, ≤ 5 MB each, 1–6 per language',
    screenshotTooLarge: 'Each screenshot must be ≤ 5 MB',
    ratioPc: 'Please upload a landscape 16:9 screenshot',
    ratioMobile: 'Please upload a portrait 9:16 screenshot',
    website: 'Official Website URL',
    privacyUrl: 'Privacy Policy URL',
    termsUrl: 'Terms of Service URL',
    supportAccount: 'Support Account',
    supportAccountEmpty: 'No matching user',
    supportAccountSpace: 'Workspace: {{name}}',
    permHint:
      'No permission changes compared to the previous version. If you need to adjust permissions, go to',
    permNoChange: 'No permission changes compared to the previous version.',
    permPending:
      '{{scope}} and {{count}} permission(s) under review, not included in this version',
    permRejected:
      '{{count}} permission(s) were rejected; related features will be unavailable.',
    goScope: 'Go to permissions',
    goScopeReapply: 'Reapply in permissions',
    permManageHint: 'To add or remove permissions, go to',
    permManage: 'Permissions',
    permApp: 'App Permissions',
    permUser: 'User Identity Permissions',
    permUserConsent: 'Users must grant access after release.',
    permSameAsPrev: 'No changes compared to the previous version.',
    permAddedGroup: 'Add Permissions ({{count}}):',
    permRemovedGroup: 'Delete Permissions ({{count}}):',
    permUnchangedGroup: 'Unmodified Permissions ({{count}}):',
    permViewAll: 'View all',
    permCollapse: 'Collapse',
    permApproved: 'Approved',
    changeDetails: 'Change Details',
    permChanges: 'Permission Changes:',
    scopeAdded: 'Added',
    scopeRemoved: 'Removed',
    eventApp: 'App Identity Events',
    eventUser: 'User Identity Events',
    eventAddedGroup: 'Added Events ({{count}}):',
    eventRemovedGroup: 'Deleted Events ({{count}}):',
    eventUnchangedGroup: 'Unmodified Events ({{count}}):',
    releaseImmediate: 'Go live immediately after approval',
    releaseScheduled: 'Scheduled release',
    schedulePlaceholder: 'Pick a date and time (at least 1 hour later)',
    willReleaseAt: 'Will automatically go live on {{time}}',
    modifyTime: 'Edit Time',
    unsavedTitle: 'Unsaved Changes',
    unsavedBody: 'Leaving this page will discard your changes. Save first?',
    dontSave: "Don't Save",
    saveChanges: 'Save Changes',
    errMainPackage: 'Please upload and parse the main package first',
    errVersionCode:
      'Version must be greater than the highest version ever released ({{max}})',
    errNoPlatformPackage: 'Upload at least one new platform package for this version',
    errFirstPlatformPackages:
      'The first version must upload packages for all platforms: {{platforms}}',
    errMissingPlatformPackage:
      'No previous package to reuse for {{platforms}}. Please upload one.',
    errUploading: 'A package is uploading. Please wait before submitting.',
    errChangelog: 'Please fill in release notes for at least one language',
    errListingCategory: 'Please select a listing workspace category',
    errOwnerSpace: 'Please select a workspace',
    errDescription: 'Please fill in the app description (English)',
    errUrl: 'Enter a valid URL starting with https://',
    errScheduleTime: 'Scheduled time must be at least 1 hour from now',
    errReviewing:
      'A version is already under review. Wait for the result or withdraw first.',
    errScheduled:
      'A version is scheduled. Submit after it goes live.',
    closeWhileUploadingTitle: 'Upload in progress',
    closeWhileUploadingContent:
      'Closing will cancel the upload and discard its progress. Continue?',
    dropUploadsContent:
      'Starting a new upload will cancel the current one and discard its progress. Continue?',
    closeWhileUploadingOk: 'Cancel Upload & Close',
    uploadFinishedBeforeClose: 'Upload finished. You can continue to configure.',
  },
  uploadPanel: {
    uploading: 'Uploading...',
    parsing: 'Parsing...',
    allSuccess: 'Upload Successful',
    partiallyFailed: 'Partially Failed',
    configure: 'Configure',
    continueConfigure: 'Continue to configure',
    waiting: 'Waiting for upload…',
    collapsed: 'Uploading ({{count}}) · {{percent}}%',
    remaining: 'About {{time}} remaining',
    remainingSoon: 'Finishing up',
    remainingSec: '{{s}} sec',
    remainingMin: '{{m}} min',
    remainingMinSec: '{{m}} min {{s}} sec',
    remainingHour: '{{h}} hr',
    remainingHourMin: '{{h}} hr {{m}} min',
  },
  credentials: {
    title: 'App Credentials',
    appId: 'App ID',
    moduleId: 'App ID',
    appSecret: 'App Secret',
    appName: 'App Name',
    appNameHint: 'Shown on the workbench and app sidebar. Takes effect on save.',
    appIcon: 'App Icon',
    appIconHint: 'PNG, JPG, or GIF, up to 2 MB',
    changeIcon: 'Change',
    nameRequired: 'Please enter an app name',
    nameTooLong: 'App name must be 128 characters or fewer',
    iconType: 'Please upload a PNG, JPG, or GIF image',
    iconSize: 'Image must be 2 MB or smaller',
    profileSaved: 'App profile saved',
    appIdHint: 'Assigned when the app is created and cannot be changed',
    moduleIdHint: 'Written to the main package manifest id. Cannot be changed after creation.',
    appSecretHint:
      'Masked by default. Showing the value requires confirmation and hides after 30s',
    resetNow: 'Reset',
    resetTitle: 'Are you sure you want to reset the App Secret?',
    confirmReset: 'Confirm Reset',
    resetHours: '{{count}} h',
    rotate: 'Secure Rotation',
    revealConfirm:
      'App Secret is sensitive. Do not share it. Show plaintext?',
    resetConfirm:
      'Upon reset, the old App Secret will be invalidated immediately, and all ongoing API calls will be interrupted. Use only in emergencies such as credential leaks. “Secure Rotation” is recommended instead.',
    copiedAppId: 'App ID copied',
    copiedModuleId: 'App ID copied',
    copiedSecret: 'App Secret copied',
    resetDone:
      'App Secret reset. Please update your code.',
    rotating: 'Rotating',
    rotateHint: 'Both secrets stay valid during the transition, no downtime',
    error: {
      '32201': 'App ID is required',
      '32313': 'You do not have access to this app',
      '32314': 'App not found',
      secretNotReady:
        'App Secret has not been generated. Publish or reset first.',
      '32300': 'Service is temporarily unavailable. Please try again later.',
      '32312': 'Internal error. Please try again later.',
      fallback: 'Failed to load. Please try again later.',
    },
  },
  scope: {
    title: 'Permissions & Scope',
    desc: 'Follow least privilege; apply for scopes as needed',
    apply: 'Apply',
    requestAccess: 'Request Access',
    requestTitle: 'Add permission scopes to app',
    name: 'Permission Name',
    scope: 'Scope',
    category: 'Category',
    categoryAll: 'All',
    identityType: 'Permission Type',
    identity_app: 'App Identity',
    identity_user: 'User Identity',
    identityTab_app: 'App Identity Permission',
    identityTab_user: 'User Identity Permission',
    sensitivity: 'Sensitivity',
    actions: 'Actions',
    relatedApis: 'Related APIs/Events',
    relatedApisColumn: 'Related APIs/events',
    searchPlaceholder: 'Search by name',
    filterPermission: 'Permission',
    filterAll: 'All',
    filterReviewYes: 'Approval required',
    filterReviewNo: 'No approval',
    empty: 'No granted permissions yet',
    hoverAccessToken: 'Access Token',
    hoverRequiresReview: 'Requires Review',
    hoverAutoApproved: 'Auto-Approved Permission',
    hoverDescription: 'Permission Description',
    token_app: 'tenant_access_token',
    token_user: 'user_access_token',
    catalogEmpty: 'No matching permissions',
    alreadyOpen: 'Already granted',
    alreadyOpenTag: 'Granted',
    approvalType: 'Approval required',
    approvalHint:
      'All permissions are auto-approved and granted immediately after you request access.',
    noReview: 'NO',
    reviewYes: 'YES',
    selectedLabel: 'Selected:',
    cancelSelection: 'Cancel Selection',
    requestSelected:
      'Selected: App Identity Permission ({{app}}) User Identity Permission ({{user}})',
    cat_userInfo: 'User Info',
    cat_messages: 'Messages',
    cat_contacts: 'Contacts',
    cat_tasks: 'Tasks',
    cat_ai: 'AI Capabilities',
    cat_files: 'Files',
    cat_calendar: 'Calendar',
    batchClose: 'Batch Disable',
    selectedCount: 'Activated: {{count}}',
    close: 'Disable',
    withdraw: 'Withdraw',
    withdrawTitle: 'Withdraw request?',
    withdrawContent: 'Withdraw the request for 「{{scopeId}}」?',
    withdrawSuccess: 'Request withdrawn',
    reapply: 'Reapply',
    delete: 'Delete',
    deleteTitle: 'Delete "{{name}}"?',
    deleteContent1:
      'Once deleted, this permission request record will be permanently removed.',
    deleteContent2:
      'To use it again, you can reapply in the "Request Access" modal.',
    deleteConfirmOk: 'Confirm Delete',
    deleteSuccess: 'Deleted "{{name}}"',
    closeTitle: 'Disable permission?',
    closeContent:
      'Disable 「{{scopeId}}」? It will not be included in the next version, and related APIs will be unavailable after that version is released.',
    batchCloseTitle: 'Disable the following {{count}} permission(s)?',
    batchCloseContent:
      'They will not be included in the next version, and related APIs will be unavailable after that version is released:',
    openSuccess:
      'Permissions granted. They will be included in the next version and can be called only after release.',
    openSuccessUser:
      'Permissions granted. They will be included in the next version and can be called only after release. User-identity permissions also require the user’s consent.',
    closeSuccess:
      'Permission disabled. It will not be included in the next version.',
    batchCloseSuccess:
      'Disabled {{count}} permission(s). They will not be included in the next version.',
    infoLead:
      'Scopes control the APIs an app can call and the range of data it can access.',
    info_release_title: 'Takes effect with a release',
    info_release_desc:
      'Both granting and disabling must be included in the next version and can be called only after release.',
    level_normal: 'Normal',
    level_elevated: 'Elevated',
    level_sensitive: 'Sensitive',
    loadFailed: 'Failed to load permissions',
    openFailed: 'Failed to grant permissions. Please try again.',
    closeFailed: 'Failed to disable. Please try again.',
    batchClosePartial:
      '{{succeeded}} disabled, {{failed}} failed. Retry the failed ones later.',
    batchCloseAllFailed: 'Failed to disable. Please try again.',
    withdrawUnavailable: 'Withdrawing a request is not available yet',
    deleteUnavailable: 'Deleting a request is not available yet',
    paginationPrev: 'Previous',
    paginationNext: 'Next',
    paginationPage: 'Page {{page}} / {{total}}',
    level: 'Sensitivity',
    status: 'Review Status',
    status_pending: 'Pending Review',
    status_approved: 'Granted',
    status_rejected: 'Rejected',
    level_basic: 'Basic',
    level_org: 'Org',
  },
  events: {
    title: 'Events & Callbacks',
    desc: 'Select events to subscribe; unselected events are not delivered',
    sendTest: 'Send Test Event',
    subscribed: 'Subscribed',
    unsubscribed: 'Not subscribed',
    event: 'Event',
    trigger: 'Trigger',
    tabConfig: 'Event Configuration',
    tabEncryption: 'Encryption Strategy',
    subscriptionMethod: 'Subscription Method',
    webhookUrl: 'Webhook URL',
    webhookHint:
      'Public URL of your server. The platform will POST events to this address.',
    urlNotConfigured: 'Subscription URL not configured',
    deliveryPersistent: 'Receive events using persistent connection',
    deliveryWebhook: 'Send events to developer server',
    urlPlaceholder: 'Enter HTTP/HTTPS URL',
    urlInvalid: 'Enter a valid HTTP/HTTPS URL',
    goEncryption: 'Go to Encryption Policy',
    saved: 'Saved',
    saveFailed: 'Save failed. Please try again later.',
    challengeFailed:
      'URL verification failed. Check that the server is reachable and responds to Challenge.',
    addedTitle: 'Events added',
    addEvents: 'Add Events',
    eventName: 'Event Name',
    eventType:"Subscription Type",
    appIdentity:"App Identity",
    subscribeType: 'Subscription Type',
    subscribeTenant: 'App identity',
    subscribeUser: 'User identity',
    actions: 'Actions',
    deleteEvent: 'Delete Event',
    batchDelete: 'Batch delete',
    deleted: 'Deleted',
    deleteFailed: 'Failed to delete the event. Please try again later.',
    added: 'Added',
    addFailed: 'Failed to add events. Please try again later.',
    versionRequired: 'There is no editable draft version.',
    addDrawerTitle: 'Add Events',
    searchPlaceholder: 'Search event name or code',
    searchEmpty: 'No matching events. Try another keyword.',
    loadFailed: 'Failed to load the event catalog. Please try again later.',
    settingLoadFailed: 'Failed to load event settings. Please try again later.',
    listLoadFailed: 'Failed to load added events. Please try again later.',
    tenantTab: 'Tenant Token-Based Subscription',
    userTab: 'User Token-Based Subscription',
    tenantHint:
      'Subscribe with tenant_access_token to receive events available to this app.',
    userHint:
      'Subscribe with user_access_token to receive events available to the signed-in user.',
    selectedCount: 'Events Selected: <count>{{count}}</count>',
    encryptionDesc:
      "Encrypt the request content for events or callbacks and validate the source of the request. It takes effect when you choose to subscribe by sending <events>events</events> or <callbacks>callbacks</callbacks> to the developer's server.",
    encryptKey: 'Encrypt Key',
    encryptInactive:
      'Unactivated. Click Reset to generate automatically, or click Edit to customize.',
    verifyToken: 'Verification Token',
    resetEncryptTitle: 'Reset Encrypt Key?',
    resetEncryptConfirm:
      'The old Encrypt Key will be invalidated immediately and your server may fail to decrypt. Update the server config first.',
    resetTokenTitle: 'Reset Verification Token?',
    resetTokenConfirm:
      'The old Verification Token will be invalidated immediately and your server may fail verification. Update the server config first.',
    confirmReset: 'Confirm Reset',
    encryptResetDone: 'Encrypt Key reset',
    tokenResetDone: 'Verification Token reset',
    resetFailed: 'Reset failed. Please try again later.',
    tokenCopied: 'Verification Token copied',
    catContact: 'Contacts',
    catOrg: 'Organization',
    catMessage: 'Messages',
    catCalendar: 'Calendar',
    catDocs: 'Docs',
    catRoom: 'Rooms',
    catFile: 'Files',
  },
  security: {
    title: 'Security',
    banner: 'The changes will take effect after the current version is published.',
    viewVersion: 'Create Version',
    tabRedirect: 'Redirect URLs',
    tabIp: 'IP allowlist',
    tabH5: 'H5 trusted domains',
    redirectTitle: 'Redirect URLs',
    redirectDesc:
      'Add redirect URLs as target addresses for password-free authorization code redirects. Other redirect URLs will not be able to retrieve authorization codes.',
    ipTitle: 'IP allowlist',
    ipDesc:
      'Only IP addresses in the allowlist can call the Open Platform APIs. Requests from IP addresses outside the allowlist will be rejected.',
    h5Title: 'H5 trusted domains',
    h5Desc:
      'Only web pages included in the trusted domains can access JSAPI, and trusted domains must be configured for JSAPI requiring authentication.',
    urlPlaceholder: 'Enter an HTTP/HTTPS URL',
    ipPlaceholder: 'Must be a valid IP',
    add: 'Add',
    batchModify: 'Batch Modify',
    colUrl: 'URL',
    colIp: 'IP',
    colDomain: 'Domain',
    colActions: 'Actions',
    invalidUrl: 'Please enter a valid HTTP/HTTPS URL',
    invalidIp: 'Please enter a valid public IPv4 address',
    duplicateUrl: 'This URL is already in the list',
    duplicateIp: 'This IP is already in the list',
    duplicateDomain: 'This domain is already in the list',
    maxReached: 'Up to {{max}} entries',
    batchHint: 'You can type or paste into the box and press Enter to wrap text. Up to {{max}} entries can be entered.',
    batchFieldRedirect: 'Redirect URLs',
    batchFieldIp: 'IP address',
    batchFieldH5: 'Trusted domains',
    batchInvalidUrl: 'Wrong URL format',
    batchInvalidIp: 'Wrong IP format',
    batchLineInvalid: 'Line {{n}} has an invalid format',
    batchSaved: 'Saved',
    batchFailed: 'Save failed. Please try again later.',
    confirmTitle: 'Confirm Changes?',
    confirmDeleted: 'You deleted the following content in this update:',
    confirmHint: 'Once confirmed, the deleted content will take effect live immediately.',
    confirmOk: 'Confirm Changes',
    deleteConfirm: 'Delete?',
    deleteOk: 'OK',
    loadFailed: 'Failed to load. Please refresh.',
    addFailed: 'Failed to add. Please try again later.',
    saveFailed: 'Save failed. Please try again later.',
    deleteFailed: 'Delete failed. Please try again later.',
  },
  developerInfo: {
    title: 'Developer Information',
    changeAvatar: 'Change Avatar',
    name: 'Developer Name',
    consoleUrl: 'Developer Console URL',
    developerId: 'Developer ID',
    nameRequired: 'Please enter a developer name',
    nameTooLong: 'Developer name must be 50 characters or fewer',
    urlInvalid: 'Please enter a valid HTTPS URL',
    saved: 'Saved',
    saveFailed: 'Save failed. Please try again later.',
    loadFailed: 'Failed to load. Please refresh.',
    avatarType: 'Please upload a JPG, PNG, or WebP image',
    avatarSize: 'Image must be 5 MB or smaller',
    cropTitle: 'Crop Avatar',
    back: 'Back',
  },
  sdk: {
    title: 'SDK Download',
    desc: 'Download official SDKs and view changelogs',
    download: 'Download',
    version: 'Version',
  },
  entry: {
    deniedTitle: 'Access denied',
    deniedDesc:
      'Enter from the HiBro client or space admin console with user_id and space_id.',
    userId: 'User ID',
    spaceId: 'Space ID',
  },
  selectPeople: {
    title: 'Select people',
    searchPlaceholder: 'Search departments or members',
    selected: 'Selected {{count}}',
    clear: 'Clear all',
    empty: 'Nothing selected',
    loadFailed: 'Failed to load. Please try again.',
  },
  notFound: {
    desc: 'Page not found',
    back: 'Back to Workbench',
  },
  errorBoundary: {
    title: 'Something went wrong',
    desc: 'Refresh or go back to the workbench and try again.',
    retry: 'Back to Workbench',
  },
  login: {
    title: 'Developer Console',
    subtitle: 'Please sign in',
    signIn: 'Sign In',
    exchanging: 'Signing in…',
    loading: 'Loading…',
    waitingHost: 'Getting session from the client…',
    misconfigured: 'VITE_AUTH_URL is not configured',
    stateMismatch: 'Invalid login state. Please try again.',
    failed: 'Sign-in failed',
    retry: 'Retry',
    hostAuthFailed: 'Could not get session from the client',
    hostAuthFailedHint: 'Make sure you are signed in to HiBro, then try again.',
  },
  placeholder: {
    building: 'This module is under construction',
  },
  products: {
    title: 'Subscription Products',
    create: 'Create Product',
    editTitle: 'Edit Product',
    colName: 'Product',
    colBilling: 'Billing',
    empty: 'No subscription products',
    limitTip: 'Product limit reached (10). Delete a draft to create another.',
    network: 'Request failed. Check your network and retry.',
    forbidden: 'No permission. Contact the app owner.',
    required: 'Please complete required fields and retry.',
    nameEmpty: 'Product name cannot be empty',
    priceMin: 'Please enter a monthly price of at least 0.01.',
    tierNameEmpty: 'Tier name cannot be empty',
    tierPriceMin: 'Please enter a price of at least 0.01.',
    expireMin: 'Please enter a validity period of at least 1.',
    moduleNameEmpty: 'Module name cannot be empty',
    name: 'Product Name',
    desc: 'Product Description',
    status: 'Status',
    billing: 'Billing Method',
    periods: 'Cycle',
    actions: 'Actions',
    draft: 'Draft',
    scheduled: 'Pending Release',
    published: 'Published',
    unlisted: 'Unlisted',
    free: 'Free',
    workspace: 'Per Workspace',
    seat: 'Per Seat',
    usage: 'Usage-based',
    module: 'Per Feature Module',
    dash: '-',
    listAt: 'Lists at {{time}} UTC',
    unlistAt: 'Delists {{time}} UTC',
    listAtSuffix: 'list',
    unlistAtSuffix: 'unlist',
    publish: 'Publish',
    listNow: 'List Now',
    cancelSchedule: 'Cancel Schedule',
    relist: 'Relist',
    unlist: 'Delist',
    duplicate: 'Copy',
    saveDraft: 'Save draft',
    saveVersion: 'Save Version',
    submitPublish: 'Submit and Publish',
    save: 'Save',
    textPh: 'Text...',
    perMonth: '/Monthly',
    perModuleMonth: '/Month',
    oneTime: 'One-time',
    effectiveHint:
      'Applies only to newly created subscription plans; effective dates for existing plans remain unchanged.',
    tierIndex: 'Tier {{n}}',
    moduleIndex: 'Module {{n}}',
    basic: 'Basic Information',
    billingConfig: 'Billing Configuration',
    periodConfig: 'Subscription Cycles',
    listing: 'Auto Listing & Delisting',
    namePh: 'Enter product name',
    descPh: 'Enter description',
    freeHint: 'Free Product: Workspaces can be used at no cost',
    freeMarketHint:
      'If no live products are configured for the app, the "Pricing Plans" section will be hidden on the client. The subscription portal will only appear after a paid product is published.',
    workspacePrice: 'Monthly Price per Workspace',
    seatPrice: 'Monthly Price per Seat',
    perSeatMonth: '/Seat/Month',
    perSeat: '/Seat',
    priceHint:
      'Price changes do not affect paid plans; new purchases and renewals (including auto-renewals) will use the latest price.',
    modulePriceHint:
      'Price changes do not affect active or paid-pending plans; new purchases and renewals (including auto-renewals) will use the latest price.',
    usagePriceHint:
      'Price changes do not affect purchased quota packs; re-purchases will use the new price.',
    effectiveMode: 'Effective Time Mode',
    afterExpiry: 'Effective Upon Expiration',
    immediate: 'Effective Immediately',
    scheduledMode: 'Effective at Scheduled Time',
    effectiveAtPh: 'Choose effective time (UTC)',
    effectiveAtFuture: 'Effective time must be in the future',
    autorenewal: 'Auto-renewal charged days in advance',
    autorenewalFixed: '2 days (Platform default, non-editable)',
    optionalPeriods: 'Available periods',
    addPeriod: 'Add Cycle',
    periodHint:
      'Client auto-calculates price based on subscription cycle: 1 month = 30 days, 1 year = 12 months.',
    periodExists: 'This period already exists',
    confirmPeriod: 'Confirm',
    unitDay: 'Days',
    unitMonth: 'Months',
    unitYear: 'Years',
    periodDay: ' days',
    periodMonth: ' months',
    periodYear: ' years',
    cardDay: '{{n}} day',
    cardDays: '{{n}} days',
    cardMonth: '{{n}} month',
    cardMonths: '{{n}} months',
    cardYear: '{{n}} year',
    cardYears: '{{n}} years',
    headDay: '{{n}} Day',
    headDays: '{{n}} Days',
    headMonth: '{{n}} Month',
    headMonths: '{{n}} Months',
    headYear: '{{n}} Year',
    headYears: '{{n}} Years',
    listMo: '{{n}} Mo',
    listMos: '{{n}} Mos',
    listYr: '{{n}} Yr',
    listYrs: '{{n}} Yrs',
    modulePriceTable:
      'Price per Module × Selected Cycle (Read-only, calculated individually)',
    tiers: 'Quota Pack Tiers',
    addTier: 'New Tier',
    tierName: 'Tier Name',
    tierDesc: 'Description',
    tierPrice: 'Tier Price',
    expire: 'Validity Period',
    expireHint:
      'Expiration time is calculated upon purchase; updates do not affect purchased quota packs.',
    keepOneTier: 'Keep at least one tier',
    modules: 'Feature Modules',
    moduleCol: 'Module',
    addModule: 'Add Module',
    moduleName: 'Module Name',
    moduleDesc: 'Description',
    monthPrice: 'Monthly Price',
    keepOneModule: 'Keep at least one module',
    listMode: 'Listing Time',
    listNowMode: 'List Immediately',
    listAtMode: 'Scheduled Time',
    scheduleAction: 'Schedule listing',
    relistNowAction: 'List',
    confirmCancelBack: 'Back',
    nameUnique: 'Names must be unique',
    listAtPh: 'Choose list time (UTC)',
    unlistMode: 'Delisting Time',
    unlistNever: 'No Auto-Delisting',
    unlistAtPh: 'Choose unlist time (UTC)',
    utc: 'UTC',
    billingLocked: 'Cannot change after publish',
    needTier: 'Add at least one quota pack tier',
    needModule: 'Add at least one feature module',
    needPeriod: 'Add at least one billing period',
    listAtFuture: 'List time must be in the future',
    unlistAfterList: 'Unlist time must be after list time',
    unlistAfterNow: 'Unlist time must be in the future',
    draftSaved: 'Draft saved',
    publishedOk: 'Product published. Workspaces can subscribe now.',
    scheduledOk: 'Scheduled to list at {{time}} UTC',
    listNowOk: 'Product listed',
    cancelOk: 'Schedule cancelled. Product is a draft again.',
    unlistOk: 'Product unlisted',
    unlistSaved: 'Will auto-unlist at {{time}} UTC',
    relistOk: 'Product relisted',
    duplicated: 'Copied as a draft. Edit it before publishing.',
    deleted: 'Product deleted',
    savePrice:
      'Saved. Paid plans are unchanged; new purchases and renewals use the new price.',
    saveBoth:
      'Saved. Paid plans are unchanged; new purchases and renewals use the new price. Other changes apply immediately.',
    saveOther: 'Saved. Changes apply immediately.',
    confirmPublishTitle: 'Publish this product?',
    confirmPublishBody:
      'After publish, workspaces can discover and subscribe to it.',
    confirmScheduleTitle: 'Schedule listing?',
    confirmScheduleBody:
      'The product will be scheduled and listed at {{time}} UTC.',
    confirmUnlistLine: 'It will auto-unlist at {{time}} UTC.',
    confirmListNowTitle: 'List now?',
    confirmListNowBody: 'Skip the scheduled time and list it immediately.',
    confirmCancelTitle: 'Cancel scheduled listing?',
    confirmCancelBody:
      'The product returns to draft and will not list automatically. The form is kept.',
    confirmCancelOk: 'Cancel schedule',
    confirmRelistTitle: 'Relist this product?',
    confirmRelistBody: 'It will appear in the marketplace again.',
    confirmRelistScheduleTitle: 'Schedule relist?',
    confirmRelistScheduleBody:
      'It will list at {{time}} UTC and appear in the marketplace then.',
    confirmUnlistTitle: 'Unlist this product?',
    confirmUnlistBody:
      'It will leave the marketplace. Existing subscribers can keep using it. Any auto-unlist schedule will be cancelled.',
    confirmUnlistOk: 'Unlist',
    confirmDeleteTitle: 'Delete this product?',
    confirmDeleteBody: 'This cannot be undone.',
    leaveTitle: 'Leave without saving?',
    leaveBody: 'Unsaved changes will be lost.',
    leaveOk: 'Leave',
    leaveCancel: 'Keep editing',
    i18nTitle: 'Other languages',
    i18nHint:
      'The client shows the matching language. If empty, the default name is used.',
    i18nEn: 'English (Default)',
    i18nZhCN: 'Simplified Chinese',
    i18nZhTW: 'Traditional Chinese',
    i18nVi: 'Vietnamese',
    expand: 'Expand',
  },
  settlements: {
    title: 'Subscription settlements',
    intro:
      'Confirmed statements add to available balance. Withdraw to HiBro wallet or another wallet.',
    firstHint:
      'Confirmed statements add to available balance. Withdraw to HiBro wallet or another wallet.',
    withdraw: 'Withdraw',
    balance: 'Available balance',
    balanceHint: 'Confirmed, not withdrawn',
    statements: 'Statements',
    empty: 'No statements yet. They are created on the settlement date.',
    emptyFilter: 'No statements match the filters',
    status: 'Status',
    billing: 'Billing',
    dateRange: 'Settlement date',
    dateStart: 'Start date',
    dateEnd: 'End date',
    all: 'All',
    pending: 'Pending',
    confirmed: 'Confirmed',
    rejected: 'Rejected',
    closed: 'Closed',
    workspace: 'Per workspace',
    seat: 'Per seat',
    usage: 'Usage pack',
    module: 'Modules',
    statementNo: 'Statement no.',
    orderNo: 'Order no.',
    appName: 'App',
    productName: 'Product',
    spec: 'Spec',
    settlementDate: 'Settlement date',
    amount: 'Developer share',
    action: 'Actions',
    detail: 'Details',
    ongoing: 'Service ongoing',
    ongoingTip: 'Service has not ended. Settlement date was reached by rule.',
    balanceFail: 'Failed to load balance. Check your network and retry.',
    loadFail: 'Failed to load. Check your network and retry.',
    insufficient: 'Insufficient available balance',
    withdrawFail: 'Withdraw failed. Check your network and retry.',
    noPerm: 'No permission',
    noPermStatement: 'No permission to view this statement',
    noPermOrder: 'No permission to view this order',
    copied: 'Copied',
    withdrawOkHibro: 'Withdrawn to HiBro wallet',
    withdrawOkExt: 'Withdrawal submitted',
    backToList: 'Back to settlements',
    detailTitle: 'Statement details',
    serviceRange: 'Service period',
    confirmedAt: 'Confirmed at',
    withdrawNote: 'Cash out via withdraw to a wallet',
    wallet: 'Wallet',
    hibro: 'HiBro wallet',
    external: 'Other wallet',
    amountLabel: 'Amount',
    amountPh: 'Enter amount',
    allBalance: 'All balance',
    available: 'Available',
    chain: 'Chain',
    chainPh: 'Select a chain',
    chainRequired: 'Select a chain',
    address: 'Wallet address',
    addressPh: 'Enter wallet address',
    addressInvalid: 'Enter a valid wallet address',
    overBalance: 'Cannot exceed available balance',
    confirmWithdraw: 'Confirm withdraw',
    ethereum: 'Ethereum',
    bnb: 'BNB Chain',
    tron: 'Tron',
  },
}

export default enUS
